import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';

export const BillingComponent = forwardRef(({ 
  cartItems, 
  orderMeta, 
  total, 
  translations, 
  isRtl,
  selectedCustomer,
  resetBill,
  onPrint // New prop to handle checkout API call
}, ref) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const shouldResetRef = useRef(false);

  // Handle afterprint event to reset bill if needed
  useEffect(() => {
    const handleAfterPrint = () => {
      setIsPrinting(false);
      console.log('Print finished/cancelled');
      if (shouldResetRef.current) {
        resetBill();
        shouldResetRef.current = false;
      }
    };

    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [resetBill]);

  const handlePrint = async () => {
    setIsPrinting(true);
    
    // Execute checkout API if provided
    if (onPrint) {
      try {
        await onPrint();
      } catch (error) {
        console.error("Error during checkout before print:", error);
        alert("Failed to save order before printing.");
        setIsPrinting(false);
        return;
      }
    }

    // Add a small delay to ensure DOM is ready/refreshed if needed
    setTimeout(() => {
      window.print();
      // Auto-clear bill after printing
      setTimeout(() => {
        if (resetBill) {
          resetBill();
        }
      }, 1000);
    }, 100);
  };

  useImperativeHandle(ref, () => ({
    handleSystemPrint: handlePrint,
    handleThermalPrint: (shouldReset = false) => {
      shouldResetRef.current = shouldReset;
      handlePrint();
    }
  }));

  return (
    <>
      <div className="no-print">
        <button 
          onClick={handlePrint}
          disabled={isPrinting || cartItems.length === 0}
          style={{
            padding: '12px 24px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isPrinting ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          <span>🖨️</span> {isPrinting ? 'Printing...' : 'Print Bill'}
        </button>
      </div>

      {/* Render the invoice into body so it's not affected by parent's no-print classes */}
      {createPortal(
        <div className="only-print">
          <PrintableInvoice
            cartItems={cartItems}
            orderMeta={orderMeta}
            total={total}
            translations={translations}
            isRtl={isRtl}
          />
        </div>,
        document.body
      )}
    </>
  );
});

export const PrintableInvoice = React.forwardRef(({ cartItems, orderMeta, total, translations, isRtl }, ref) => {
  const dateStr = new Date().toLocaleDateString('en-GB');
  const timeStr = new Date().toLocaleTimeString('en-US');

  return (
    <div ref={ref} className={`receipt-container ${isRtl ? 'rtl' : 'ltr'}`}>
      <style>{`
        @page {
  size: 72mm auto;
  margin: 0mm;
}
   .buttomSpace {
    visibility: hidden !important;
    width: 72mm;
      height: 200px;
    }
  @media print {
   @page {
    size: 72mm;
    margin: 0 !important;
  }
     
   
  .receipt-container {
    width: 72mm !important;
    max-width: 72mm !important;
    padding: 0 3mm !important; /* Reduced padding for more width */
    margin: 0  auto !important;
    overflow: visible !important;
    position: static !important;
  }
  
  .bottom-space {
    height: 60mm !important; /* Increased bottom space */
    width: 100% !important;
    display: block !important;
    visibility: visible !important;
    color: white !important;
    page-break-after: always !important;
  }
}
       .receipt-container {
  width: 72mm;
  max-width: 72mm;
  margin: 0;
  padding: 0 2mm; /* Changed from padding: 1mm */
  background: white;
  color: black;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.2;
  box-sizing: border-box; /* Important: includes padding in width calculation */
}
        /* Typography */
        h2 { font-size: 16px; margin: 0; text-align: center; font-weight: 800; text-transform: uppercase; }
        p { margin: 2px 0; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .bold { font-weight: bold; }
        
        /* Layout */
        .divider { 
  border-bottom: 1px dashed #000; 
  margin: 4px 2mm; 
}
.double-divider { 
  border-bottom: 2px solid #000; 
  margin: 4px 2mm; 
}
      .meta-info {
  display: block;
  text-align: left;
  width: 100%;
  padding: 0 2mm;
  margin: 2px 0;
}
      /* Table */
table { 
  width: 100%; 
  border-collapse: collapse;
  table-layout: fixed;
}
th { 
  text-align: left; 
  border-bottom: 0.6px solid #000; 
  border: 0.6px solid #000;
  padding: 2px 6px; 
  font-size: 11px; /* Increased font size */
  font-weight: bold;
}
td { 
  vertical-align: top; 
  padding: 2px 4px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: 14px; /* Increased font size */
  border: 0.6px solid #000;
}

.col-item { 
  width: 35%; 
  max-width: 35%;
  word-break: break-word;
}
.col-price { 
  width: 18%; 
  text-align: right; 
}
.col-qty { 
  width: 12%; 
  text-align: center; 
}
.col-bags { 
  width: 12%; 
  text-align: center; 
}
.col-total { 
  width: 23%; 
  text-align: right; 
}

/* RTL Support */
.rtl { direction: rtl; }
.rtl th { text-align: right; }
.rtl td { text-align: right; }
.rtl .col-price, .rtl .col-total { text-align: left; }
.rtl .col-qty, .rtl .col-bags { text-align: center; }

/* Footer */
.footer { 
  margin-top: 8px; 
  padding: 0 2mm;
  font-size: 13px; 
  text-align: center;
  margin-bottom: 8px; /* Add margin after footer */
}

/* Header/Store Info */
.receipt-container > .text-center {
  padding: 0 2mm;
  margin-bottom: 8px; /* Add margin after header */
}

/* Total Section */
.receipt-container > div[style*="display: flex"] {
  padding: 0 2mm;
  margin: 8px 0; /* Add margin before and after total */
}

/* Table row spacing */
tbody tr {
  margin-bottom: 0; /* Remove margin between table rows */
}

tbody tr:last-child {
  margin-bottom: 0; /* Remove margin from last row */
}
      `}</style>

      <div className="text-center">
           <br />
        <h2>{translations.storeName || 'Azeem Karyana'}</h2>
           <br />
        <p>{translations.Ariwala || 'General Store'}</p>
        <p className="bold">
          {translations.date || 'Date'}: {dateStr}
          <br />
          {translations.time || 'Time'}: {timeStr}
        </p>
       
      </div>
   <br />
      <br />
      <div className="double-divider"></div>
      
      {orderMeta.customerName && (
        
        <div className="meta-info" style={{ marginTop: '2px' }}>
           <br />
         <span>{translations.customer}: {orderMeta.customerName}</span>
        </div>
      )}
        
      <div className="divider"></div>

      <table>
        <thead>
          <tr>
            <th className="col-item">{translations.item || 'Item'}</th>
            <th className="col-price">{translations.sellPrice || 'Price'}</th>
            <th className="col-qty">{translations.qty || 'Qty'}</th>
            <th className="col-bags">{translations.bags || 'Bags'}</th>
            <th className="col-total">{translations.total || 'Total'}</th>
          </tr>
        </thead>
        <tbody>
          {cartItems.map((item, index) => {
            const qty = Number(item.qty || 0);
            const bagWeightRaw = Number(item.bagWeight);
            const bagWeight = Number.isFinite(bagWeightRaw) && bagWeightRaw > 0 ? bagWeightRaw : 0;
            const itemPrice = Number(item.sellPrice || 0);
            
            let displayBags = 0;
            let displayQty = qty;
            
            if (bagWeight > 0) {
              displayBags = Math.floor(qty / bagWeight);
              displayQty = qty % bagWeight;
            }
            
            // Calculate total based on bags and remaining quantity
            const bagTotal = displayBags * bagWeight * itemPrice;
            const qtyTotal = displayQty * itemPrice;
            const itemTotal = bagTotal + qtyTotal;
            
            return (
              <>
               <tr  key={index}>
                <td className="col-item">{item.name}</td>
                <td className="col-price">{itemPrice.toFixed(0)}</td>
                <td className="col-qty">{displayQty}</td>
                <td className="col-bags">{displayBags}</td>
                <td className="col-total">{itemTotal.toFixed(0)}</td>
              </tr>
              </>
             
            );
          })}
        </tbody>
      </table>

      <div className="double-divider"></div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold' }}>
        <span>{translations.total || 'TOTAL'}:</span>
        <span>{total.toFixed(2)}</span>
      </div>

      <div className="divider"></div>

      <div className="footer">
        <p>{translations.thanks || 'Thank you for shopping!'}</p>
           <br />
        <p>Developed by DotTech</p>
         
        <p className="bold">03006911475</p>
      </div>
      
      {/* Bottom space for printer cutting */}
      <div className="bottom-space">
        
        <br />
        ............................. 
      </div>
    </div>
  );
});